import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { getPostBySlug, formatPostDate } from "../lib/blogPosts";
import { usePageMeta, SITE_URL } from "../hooks/usePageMeta";
import { useStructuredData } from "../hooks/useStructuredData";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  usePageMeta({
    title: post ? `${post.title} | Just Dessert Me Blog` : "Post not found | Just Dessert Me Blog",
    description: post?.excerpt || "This blog post could not be found.",
    path: `/blog/${slug}`,
    image: post?.image || undefined,
  });
  useStructuredData(
    "blog-post",
    post && {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      datePublished: post.date,
      url: `${SITE_URL}/blog/${post.slug}`,
      ...(post.image ? { image: post.image } : {}),
      author: { "@type": "Organization", name: "Just Dessert Me" },
      publisher: { "@type": "Organization", name: "Just Dessert Me" },
    }
  );

  if (!post) {
    return (
      <main data-testid="page-blog-post-missing" className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h1 className="font-script text-5xl text-white mb-4">Post not found</h1>
        <p className="text-gray-400 mb-8">That post doesn't exist (or has been removed).</p>
        <Link to="/blog" className="neon-btn">Back to the blog</Link>
      </main>
    );
  }

  return (
    <main data-testid="page-blog-post" className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
      <Link
        to="/blog"
        data-testid="blog-post-back"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gray-400 hover:text-[#00b4ff] transition-colors mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> All posts
      </Link>

      <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-3">
        <CalendarDays className="w-3.5 h-3.5 text-[#00b4ff]" />
        {formatPostDate(post.date)}
      </p>
      <h1 data-testid="blog-post-title" className="font-script text-4xl sm:text-5xl lg:text-6xl neon-gradient-text leading-tight mb-8">
        {post.title}
      </h1>

      {post.image && (
        <div className="rounded-3xl overflow-hidden border border-white/10 mb-10">
          <img src={post.image} alt={post.title} className="w-full object-cover" />
        </div>
      )}

      <article data-testid="blog-post-body" className="space-y-5">
        {post.paragraphs.map((para, i) =>
          para.startsWith("## ") ? (
            <h2 key={i} className="font-script text-3xl text-[#ff1a8c] pt-4">
              {para.slice(3)}
            </h2>
          ) : (
            <p key={i} className="text-gray-300 leading-relaxed">
              {para}
            </p>
          )
        )}
      </article>

      <div className="mt-14 glass rounded-3xl border border-[#ff1a8c]/30 p-8 text-center">
        <h3 className="font-script text-3xl text-white mb-2">Craving something sweet?</h3>
        <p className="text-sm text-gray-400 mb-5">Cookie Pies and Loaded Cookies, delivered NZ-wide.</p>
        <Link to="/shop" data-testid="blog-post-shop-cta" className="neon-btn neon-btn--blue">
          Shop desserts
        </Link>
      </div>
    </main>
  );
}
